import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {disableCanvasArrowKeys} from '~/actions/CanvasActions';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ColorSwatch from './ColorSwatch';
import Color from '~/data/records/Color';
import {isNamesOrNumbers} from '~/data/accessors/icon';
import {iconTypeToFriendlyName} from '~/utils/icon';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {metrics} from '~/vendor/metrics';
import {ColorSwatchGroup} from '@customink/pigment-react';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const ColorSwatches = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const className = classnames('ndx-ColorSwatches', props.contentClassName);
  const isWrapped = props.lineWrap;
  const [showSubtleColors, onSubtleColorLink] = useState(false);

  const handleSubtleColorLink = () => {
    onSubtleColorLink(true);
    props.selectedIconType &&
      metrics.fireEvent(
        'subtlety in lab',
        'open color drawer',
        isNamesOrNumbers(props.selectedIconType)
          ? 'personalization'
          : iconTypeToFriendlyName(props.selectedIconType).toLowerCase()
      );
  };

  const getTrim = color => {
    return color.secondary && color.secondary.hexString !== undefined
      ? `#${color.secondary.hexString}`
      : undefined;
  };

  const swatchData = props.colors.map(color => {
    return {
      color: color.hexString === '00000000' ? undefined : `#${color.hexString}`,
      trimRgb: getTrim(color),
      dogEar: props.isBookmarked(color),
      startSelected: props.isSelected(color),
      subtlety: props.isSubtle(color),
      value: color
    };
  });

  const onSwatchClick_cb = ({value}) => {
    props.onSwatchClick(
      null,
      value,
      props.isSelected(value),
      props.isBookmarked(value)
    );
  };

  const onSwatchMouseEnter_cb = ({value}) => {
    props.onHoverOn && props.onHoverOn(value);
  };

  const onSwatchMouseLeave_cb = () => {
    props.onHoverOff && props.onHoverOff();
  };

  const onFocus = () => {
    dispatch(disableCanvasArrowKeys(true));
  };

  const onBlur = () => {
    dispatch(disableCanvasArrowKeys(false));
  };

  useEffect(() => {
    return () => dispatch(disableCanvasArrowKeys(false));
  }, [dispatch]);

  return (
    <div className={className}>
      <ColorSwatchGroup
        swatchData={swatchData}
        subtletyToggle={
          !showSubtleColors && breakpoint(MediaQueries.largeAndAbove)
        }
        sx={props.sx}
        size={props.size}
        onSwatchMouseEnter={onSwatchMouseEnter_cb}
        onSwatchMouseLeave={onSwatchMouseLeave_cb}
        onSwatchSelect={onSwatchClick_cb}
        onToggleSubtletySwatches={handleSubtleColorLink}
        onFocus={onFocus}
        onBlur={onBlur}
        rowCount={
          breakpoint(MediaQueries.largeAndAbove) || isWrapped
            ? 0
            : swatchData.length
        }
        ref={ref}
      />
    </div>
  );
});

ColorSwatches.displayName = 'ColorSwatches';

const propTypes = {
  colors: PropTypes.arrayOf(ColorSwatch.propTypes.color).isRequired,
  backgroundColor: PropTypes.instanceOf(Color),
  contentClassName: PropTypes.string,
  getKey: PropTypes.func,
  hasAltTransparentIcon: PropTypes.bool,
  isBookmarked: PropTypes.func,
  isSelected: PropTypes.func,
  isSubtle: PropTypes.func,
  lineWrap: PropTypes.bool,
  noOverflow: PropTypes.bool,
  selectedColor: PropTypes.any,
  selectedIconType: PropTypes.string,
  size: PropTypes.string,
  subtleColors: PropTypes.array,
  sx: PropTypes.any,
  value: PropTypes.any,
  onHoverOff: PropTypes.func,
  onHoverOn: PropTypes.func,
  onSwatchClick: PropTypes.func
};

ColorSwatches.propTypes = propTypes;

const DEFAULT_BG = new Color({r: 0, g: 0, b: 0});

ColorSwatches.defaultProps = {
  getKey: color => color.id,
  isBookmarked: () => false,
  isSelected: () => false,
  isSubtle: () => false,
  noOverflow: false,
  onSwatchClick() {},
  size: 'small',
  subtleColors: [],
  sx: {},
  backgroundColor: DEFAULT_BG
};

export default ColorSwatches;
export {default as ColorSwatch} from './ColorSwatch';
